import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { theme, forms, motion, type Theme } from '../styles/index.js';
import {
  starFilled,
  starOutline,
  marketplace as marketplaceIcon,
  activity as activityIcon,
  search as searchIcon,
  keyboard as keyboardIcon,
  palette,
  packageBox,
  plug,
  appDefault,
  warning as warningIcon,
  xMark,
  docs,
  shieldCheck,
  settings as settingsIcon,
  source as sourceIcon,
  plus,
} from '../icons.js';
import { elicit } from '../utils/elicit.js';

interface PhotonItem {
  name: string;
  description: string;
  isApp?: boolean;
  appEntry?: any;
  methods?: any[];
  icon?: string;
  internal?: boolean;
  version?: string;
  resourceCount?: number;
  promptCount?: number;
  hasUpdate?: boolean;
  path?: string;
  /** Why this photon needs attention */
  errorReason?: 'missing-config' | 'load-error';
  // External MCP fields
  isExternalMCP?: boolean;
  connected?: boolean;
  errorMessage?: string;
  // MCP Apps Extension fields
  hasMcpApp?: boolean;
  mcpAppUri?: string;
  mcpAppUris?: string[];
  /** True if photon has property-based settings */
  hasSettings?: boolean;
}

@customElement('beam-sidebar')
export class BeamSidebar extends LitElement {
  static styles = [
    theme,
    forms,
    motion,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        color: var(--t-primary);
        overflow: visible;
        container-type: inline-size;
        container-name: sidebar;
      }

      .sidebar-content {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .sidebar-scroll {
        flex: 1;
        overflow-y: auto;
        padding-right: 4px;
        scrollbar-gutter: stable;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--space-xl) var(--space-md);
        text-align: center;
        flex: 1;
      }

      .empty-icon {
        font-size: var(--text-3xl);
        margin-bottom: var(--space-sm);
        opacity: 0.6;
      }

      .empty-title {
        font-weight: 600;
        font-size: var(--text-md);
        color: var(--t-primary);
        margin-bottom: var(--space-xs);
      }

      .empty-hint {
        font-size: var(--text-sm);
        color: var(--t-muted);
        line-height: 1.4;
      }

      .empty-action {
        margin-top: var(--space-md);
        padding: var(--space-sm) var(--space-md);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-sm);
        color: var(--accent-primary);
        font-size: var(--text-md);
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .empty-action:hover {
        background: var(--bg-glass-strong);
        border-color: var(--accent-primary);
      }

      .sidebar-footer {
        padding: var(--space-sm) var(--space-md);
        border-top: 1px solid var(--border-glass);
        display: flex;
        justify-content: center;
        gap: var(--space-xs);
        flex-wrap: wrap;
      }

      .footer-link {
        color: var(--t-muted);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.2s;
        background: none;
        border: none;
        padding: 6px;
        border-radius: var(--radius-sm);
      }

      .footer-link:hover {
        color: var(--t-primary);
        background: var(--bg-glass);
      }

      .footer-link kbd {
        font-size: var(--text-2xs);
        padding: 1px 4px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-xs);
        font-family: var(--font-mono);
      }

      .header {
        padding: var(--space-md);
        border-bottom: 1px solid var(--border-glass);
        flex-shrink: 0;
      }

      .header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: 700;
        margin: 0;
        padding: 0;
        border: none;
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        user-select: none;
        transition: opacity 0.15s;
        white-space: nowrap;
        min-width: 0;
      }

      .logo:hover {
        opacity: 0.8;
      }

      .status-indicator {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .status-indicator.connected {
        background: #22dd44;
        box-shadow:
          0 0 4px 2px rgba(34, 221, 68, 0.6),
          0 0 10px 4px rgba(34, 221, 68, 0.3);
      }

      .status-indicator.reconnecting {
        background: #ffbb33;
        box-shadow:
          0 0 4px 2px rgba(255, 187, 51, 0.6),
          0 0 10px 4px rgba(255, 187, 51, 0.3);
        animation: pulse 1s ease-in-out infinite;
      }

      .status-indicator.disconnected {
        background: #ff4444;
        box-shadow:
          0 0 4px 2px rgba(255, 68, 68, 0.6),
          0 0 10px 4px rgba(255, 68, 68, 0.3);
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.4;
        }
      }

      .theme-toggle {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-full);
        padding: 4px;
        cursor: pointer;
      }

      .theme-btn {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        transition: all 0.2s;
      }

      .theme-btn.active {
        background: var(--accent-primary);
        color: white;
      }

      .theme-btn:not(.active) {
        color: var(--t-muted);
      }

      .theme-btn:hover:not(.active) {
        background: var(--bg-glass-strong);
      }

      .search-row {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        margin-top: var(--space-md);
      }

      .search-box {
        flex: 1;
        position: relative;
      }

      .create-btn {
        flex-shrink: 0;
        margin-top: 0 !important;
      }

      .sidebar-minimize-btn {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-sm);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        padding: 0;
        flex-shrink: 0;
      }

      .sidebar-minimize-btn:hover {
        color: var(--t-primary);
        background: var(--bg-glass-strong);
        border-color: color-mix(in srgb, var(--accent-primary) 30%, var(--border-glass));
      }

      input {
        padding: var(--space-sm) var(--space-md);
      }

      .section-header {
        font-family: var(--font-display);
        padding: var(--space-xs) var(--space-md);
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--t-muted);
        opacity: 0.75;
        margin-top: var(--space-md);
        border-left: none;
        background: color-mix(in srgb, var(--t-muted) 8%, transparent);
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: var(--space-xs);
      }

      .section-header:hover {
        opacity: 1;
        color: var(--t-primary);
      }

      .section-header.attention {
        color: var(--color-warning);
      }

      .section-header.attention:hover {
        color: var(--color-warning);
        opacity: 1;
      }

      .section-label {
        flex: 1;
      }

      .section-count {
        font-size: var(--text-2xs);
        opacity: 0.55;
        margin-left: auto;
        padding-right: var(--space-xs);
      }

      .section-chevron {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        transition: transform 0.2s ease;
      }

      .section-chevron.collapsed {
        transform: rotate(-90deg);
      }

      .photon-list {
        list-style: none;
        padding: 2px var(--space-sm) 0;
        margin: 0;
        overflow: hidden;
        transition: max-height 0.2s ease;
      }

      .photon-list.collapsed {
        max-height: 0 !important;
        padding-top: 0;
        padding-bottom: 0;
      }

      .photon-item {
        /* Right padding intentionally tight (4px) — the counts pill is the
           visual end of the row. Anything more reads as wasted space and
           steals room from the photon name. */
        padding: var(--space-sm) 4px var(--space-sm) var(--space-md);
        margin-bottom: var(--space-xs);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: var(--space-sm);
      }

      .photon-item:hover {
        background: var(--bg-glass);
      }

      .photon-item.active {
        background: linear-gradient(90deg, var(--glow-primary), transparent);
        border-left: 2px solid var(--accent-primary);
        color: var(--accent-primary);
        font-weight: 500;
        align-items: center;
        padding-top: calc(var(--space-sm) + 2px);
        padding-bottom: calc(var(--space-sm) + 2px);
      }

      .photon-item.flash-highlight:not(.active) {
        animation: flash-highlight 0.6s ease-out;
      }

      @keyframes flash-highlight {
        0% {
          background: var(--glow-primary);
        }
        100% {
          background: transparent;
        }
      }

      .photon-item.warmth:not(.active) {
        animation: warmth-fade 5s ease-out forwards;
      }

      @keyframes warmth-fade {
        0% {
          background: hsla(38, 100%, 50%, 0.15);
        }
        100% {
          background: transparent;
        }
      }

      .photon-icon {
        width: 28px;
        height: 28px;
        border-radius: var(--radius-sm);
        background: var(--bg-glass-strong);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
        color: var(--t-primary);
        flex-shrink: 0;
        letter-spacing: 0.5px;
        transition:
          width 0.25s ease,
          height 0.25s ease,
          font-size 0.25s ease;
      }

      .photon-icon.emoji-icon {
        background: transparent;
        font-size: 20px;
        overflow: hidden;
      }

      /* Scale icon up when the row is expanded with view tabs */
      .photon-item.active .photon-icon {
        width: 42px;
        height: 42px;
        font-size: 15px;
      }

      .photon-item.active .photon-icon.emoji-icon {
        font-size: 30px;
      }

      .photon-item.internal {
        opacity: 0.85;
      }

      .internal-badge {
        font-size: var(--text-2xs);
        padding: 1px 4px;
        background: linear-gradient(135deg, hsl(280, 60%, 50%), hsl(320, 60%, 50%));
        color: white;
        border-radius: var(--radius-xs);
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.03em;
      }

      .photon-info {
        min-width: 0;
        flex: 1;
      }

      .photon-item-body {
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .photon-main-row {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        min-width: 0;
      }

      .photon-main-row .photon-info {
        min-width: 0;
      }

      .photon-name {
        font-family: var(--font-display);
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .photon-desc {
        font-size: 0.8em;
        color: var(--t-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .photon-view-tabs {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .photon-view-tab {
        width: 26px;
        height: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--border-glass);
        background: color-mix(in srgb, var(--bg-glass-strong) 82%, transparent);
        color: var(--t-muted);
        border-radius: var(--radius-sm);
        padding: 0;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .photon-view-tab svg {
        width: 13px;
        height: 13px;
      }

      .photon-view-tab:hover {
        color: var(--t-primary);
        border-color: color-mix(in srgb, var(--accent-primary) 38%, var(--border-glass));
        background: color-mix(in srgb, var(--accent-primary) 10%, var(--bg-glass-strong));
      }

      .photon-view-tab.active {
        color: white;
        border-color: var(--accent-primary);
        background: color-mix(in srgb, var(--accent-primary) 78%, black 22%);
      }

      .method-count {
        font-size: var(--text-2xs);
        padding: 2px 6px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        border-radius: var(--radius-full);
        font-weight: 500;
        flex-shrink: 0;
        min-width: 28px;
        text-align: center;
        box-sizing: border-box;
      }

      .method-count.unconfigured {
        background: var(--color-warning-bg);
        border-color: var(--color-warning-glow);
        color: var(--color-warning);
      }

      .method-count.error {
        background: var(--color-error-bg);
        border-color: var(--color-error-glow);
        color: var(--color-error);
      }

      .counts-pill {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        font-size: var(--text-2xs);
        padding: 2px 5px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-full);
        font-weight: 500;
        flex-shrink: 0;
        min-width: 26px;
        box-sizing: border-box;
      }

      .count-tools {
        color: hsl(210, 80%, 65%);
      }
      .count-prompts {
        color: hsl(140, 60%, 55%);
      }
      .count-resources {
        color: hsl(30, 80%, 60%);
      }
      .count-sep {
        color: var(--t-muted);
        opacity: 0.4;
        font-size: 0.55em;
      }

      .version-badge {
        font-size: var(--text-2xs);
        color: var(--t-muted);
        opacity: 0.7;
      }

      .update-dot {
        position: absolute;
        top: -3px;
        right: -3px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--accent-secondary);
        pointer-events: none;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .update-badge {
        font-size: var(--text-2xs);
        padding: 1px 5px;
        background: hsl(0, 80%, 55%);
        color: white;
        border-radius: var(--radius-sm);
        font-weight: 600;
        flex-shrink: 0;
      }

      .approval-badge-btn {
        position: relative;
      }

      .approval-badge-btn.disabled {
        opacity: 0.4;
        cursor: default;
      }

      .approval-badge-btn.disabled:hover {
        color: var(--t-muted);
        background: none;
      }

      .approval-badge {
        position: absolute;
        top: -2px;
        right: -2px;
        font-size: 9px;
        min-width: 14px;
        height: 14px;
        line-height: 14px;
        text-align: center;
        padding: 0 3px;
        background: hsl(30, 90%, 50%);
        color: white;
        border-radius: 7px;
        font-weight: 700;
      }

      .marketplace-btn {
        width: 100%;
        margin-top: var(--space-md);
        padding: var(--space-sm);
        background: var(--bg-glass-strong);
        border: 1px solid var(--border-glass);
        color: var(--t-primary);
        cursor: pointer;
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-sm);
        font-weight: 500;
        transition: all 0.2s ease;
      }

      .marketplace-btn:hover {
        background: var(--bg-panel);
        border-color: var(--accent-secondary);
        box-shadow: var(--shadow-sm);
        transform: translateY(-1px);
      }

      .empty-section {
        padding: var(--space-sm) var(--space-md);
        color: var(--t-muted);
        font-size: var(--text-md);
        font-style: italic;
      }

      /* External MCPs */
      .photon-icon.external-mcp-icon {
        background: linear-gradient(135deg, hsl(200, 60%, 45%), hsl(220, 60%, 55%));
        color: white;
        border-radius: var(--radius-sm);
        font-size: 14px;
      }

      .photon-item.disconnected {
        opacity: 0.6;
      }

      .photon-item.disconnected .photon-icon {
        background: var(--bg-glass);
        color: var(--t-muted);
      }

      .disconnect-badge {
        font-size: var(--text-2xs);
        padding: 1px 4px;
        background: hsla(0, 60%, 50%, 0.2);
        color: hsl(0, 60%, 55%);
        border: 1px solid hsla(0, 60%, 50%, 0.3);
        border-radius: var(--radius-xs);
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.03em;
      }

      .reconnect-btn {
        font-size: var(--text-2xs);
        padding: 2px 6px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        border-radius: var(--radius-xs);
        cursor: pointer;
        transition: all 0.2s;
      }

      .reconnect-btn:hover {
        background: var(--bg-glass-strong);
        color: var(--t-primary);
        border-color: var(--accent-primary);
      }

      /* Favorites */
      .filter-row {
        display: flex;
        gap: var(--space-sm);
        margin-top: var(--space-sm);
        min-width: 0;
      }

      .filter-btn {
        flex: 1 1 0;
        min-width: 0;
        padding: var(--space-xs) var(--space-sm);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        cursor: pointer;
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-size: var(--text-sm);
        white-space: nowrap;
        overflow: hidden;
        transition: all 0.2s;
      }

      .filter-btn-label {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .filter-btn:hover {
        background: var(--bg-glass-strong);
      }

      .filter-btn.active {
        background: var(--accent-primary);
        border-color: var(--accent-primary);
        color: white;
      }

      /* Favorites count is rendered centered inside the star itself —
         no separate badge, no horizontal space stolen from the label. */
      .filter-icon-wrap {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: var(--color-warning, #f59e0b);
      }

      .filter-icon-wrap svg {
        width: 22px;
        height: 22px;
      }

      .filter-count-in-star {
        position: absolute;
        top: 56%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 10px;
        font-weight: 800;
        line-height: 1;
        color: var(--bg-elevated, #0b1018);
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.04em;
        pointer-events: none;
      }

      .filter-btn.active .filter-icon-wrap {
        color: white;
      }
      .filter-btn.active .filter-count-in-star {
        color: var(--accent-primary);
      }

      /* Responsive narrow mode: shrink the title to "Beam" and switch
         the three filter buttons to icon-only when the sidebar itself
         (not the viewport) is narrow. Container query watches :host's
         inline-size, so it fires whenever the user drags the sidebar
         below the threshold regardless of window width. */
      @container sidebar (max-width: 280px) {
        .logo-prefix {
          display: none;
        }
        .filter-btn {
          position: relative;
          padding: var(--space-xs);
          gap: 0;
        }
        .filter-btn .filter-btn-label {
          display: none;
        }
        .filter-btn .update-badge {
          position: absolute;
          top: 2px;
          right: 4px;
          min-width: 14px;
          height: 14px;
          padding: 0 4px;
          font-size: 9px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid var(--bg-elevated, #0b1018);
          border-radius: 999px;
        }
      }

      .settings-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        font-size: var(--text-sm);
        opacity: 0.25;
        transition: all 0.2s;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
      }

      .photon-item:hover .settings-btn,
      .settings-btn:focus-visible {
        opacity: 0.5;
      }

      .settings-btn:hover {
        opacity: 0.8 !important;
        transform: scale(1.1);
      }

      .star-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 1px;
        font-size: var(--text-md);
        color: var(--t-muted);
        opacity: 0.5;
        transition: all 0.2s;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
      }

      .star-btn:hover,
      .star-btn:focus-visible {
        opacity: 0.9;
        transform: scale(1.1);
      }

      .star-btn.favorited {
        opacity: 1;
        color: var(--color-warning);
      }

      .photon-item .star-btn {
        opacity: 0.45;
      }

      .photon-item:hover .star-btn,
      .photon-item:focus-within .star-btn {
        opacity: 0.7;
      }

      .photon-item .star-btn.favorited {
        opacity: 1;
      }

      /* ===== Responsive Design ===== */
      @media (max-width: 768px) {
        .header {
          padding: var(--space-lg) var(--space-md);
          padding-top: calc(var(--space-lg) + 8px);
        }

        .photon-item {
          padding: var(--space-md);
          min-height: 44px;
        }

        .photon-view-tabs {
          margin-left: 0;
        }

        .photon-item .star-btn {
          padding: var(--space-sm);
          opacity: 0.4;
        }

        .filter-btn {
          min-height: 44px;
          font-size: 0.85rem;
        }

        input {
          min-height: 44px;
          font-size: 16px; /* Prevent iOS zoom on focus */
        }

        .theme-btn {
          width: 36px;
          height: 36px;
        }

        .footer-link {
          min-height: 44px;
          min-width: 44px;
        }
      }
    `,
  ];

  @property({ type: Array })
  photons: PhotonItem[] = [];

  @property({ type: Array })
  externalMCPs: PhotonItem[] = [];

  @property({ type: String })
  selectedPhoton: string | null = null;

  @property({ type: String })
  theme: Theme = 'dark';

  @property({ type: Boolean })
  connected = false;

  @property({ type: Boolean })
  reconnecting = false;

  @property({ type: Number })
  updatesAvailable = 0;

  @property({ type: Number })
  pendingApprovals = 0;

  @property({ type: String })
  mainTab = 'methods';

  @property({ type: Boolean })
  isApp = false;

  @property({ type: Boolean })
  hasSettings = false;

  /** True when the photon has constructor params that map to env vars. */
  @property({ type: Boolean })
  hasSetup = false;

  @property({ type: Boolean })
  isExternalMCP = false;

  @property({ type: Boolean })
  hasPath = false;

  @state()
  private _searchQuery = '';

  @state()
  private _showFavoritesOnly = false;

  @state()
  private _favorites: Set<string> = new Set();

  @query('input')
  private _searchInput!: HTMLInputElement;

  private static FAVORITES_KEY = 'beam-favorites';
  private static RECENT_KEY = 'beam-recent-photons';
  private static COLLAPSED_KEY = 'beam-sidebar-collapsed';

  @state()
  private _collapsedSections: Set<string> = new Set();

  /** Live recency list — saved to localStorage but NOT used for rendering.
   *  Only applied on next page load via _initialRecentOrder. */
  private _recentPhotons: string[] = [];

  /** Snapshot of recency order captured at page load — used for sorting.
   *  Stays stable during the session so the sidebar doesn't jump around. */
  private _initialRecentOrder: string[] = [];

  @state()
  private _notificationWarmth: Map<string, number> = new Map();

  connectedCallback() {
    super.connectedCallback();
    this._loadFavorites();
    this._loadRecent();
    this._loadCollapsed();
    this._ensureActiveSectionOpen();
  }

  private _loadFavorites() {
    try {
      const stored = localStorage.getItem(BeamSidebar.FAVORITES_KEY);
      if (stored) {
        this._favorites = new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('Failed to load favorites:', e);
    }
  }

  private _loadRecent() {
    try {
      const stored = localStorage.getItem(BeamSidebar.RECENT_KEY);
      if (stored) {
        this._recentPhotons = JSON.parse(stored);
        this._initialRecentOrder = [...this._recentPhotons];
      }
    } catch {
      // ignore
    }
  }

  trackRecentPhoton(name: string) {
    const recent = [name, ...this._recentPhotons.filter((n) => n !== name)].slice(0, 20);
    this._recentPhotons = recent;
    try {
      localStorage.setItem(BeamSidebar.RECENT_KEY, JSON.stringify(recent));
    } catch {
      // ignore
    }
  }

  private _saveFavorites() {
    try {
      localStorage.setItem(BeamSidebar.FAVORITES_KEY, JSON.stringify([...this._favorites]));
    } catch (e) {
      console.warn('Failed to save favorites:', e);
    }
  }

  private _loadCollapsed() {
    try {
      const raw = localStorage.getItem(BeamSidebar.COLLAPSED_KEY);
      this._collapsedSections = new Set(raw ? JSON.parse(raw) : []);
    } catch {
      this._collapsedSections = new Set();
    }
  }

  private _saveCollapsed() {
    localStorage.setItem(BeamSidebar.COLLAPSED_KEY, JSON.stringify([...this._collapsedSections]));
  }

  private _toggleSection(key: string) {
    const next = new Set(this._collapsedSections);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this._collapsedSections = next;
    this._saveCollapsed();
  }

  private _ensureActiveSectionOpen() {
    if (!this.selectedPhoton) return;
    const sectionMap: [string, PhotonItem[]][] = [
      ['apps', this._apps],
      ['photons', this._configured],
      ['config', this._needsSetup.filter((p) => p.errorReason !== 'load-error')],
      ['errors', this._needsSetup.filter((p) => p.errorReason === 'load-error')],
      ['mcps', this._nonAppExternalMCPs],
    ];
    for (const [key, items] of sectionMap) {
      if (items.some((p) => p.name === this.selectedPhoton)) {
        if (this._collapsedSections.has(key)) {
          const next = new Set(this._collapsedSections);
          next.delete(key);
          this._collapsedSections = next;
          this._saveCollapsed();
        }
        break;
      }
    }
  }

  updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);
    if (changedProps.has('selectedPhoton')) {
      this._ensureActiveSectionOpen();
      if (this.selectedPhoton) {
        this._scrollPhotonIntoView(this.selectedPhoton, false);
      } else {
        this._scrollSidebarToTop();
      }
    }
  }

  private get _filteredPhotons() {
    // Exclude internal photons — their static methods appear in the marketplace/global area
    let filtered = this.photons.filter((p) => !p.internal);

    // Filter by favorites if enabled
    if (this._showFavoritesOnly) {
      filtered = filtered.filter((p) => this._favorites.has(p.name));
    }

    // Filter by search query
    if (this._searchQuery.trim()) {
      const query = this._searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          (p.description && p.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }

  private get _apps(): PhotonItem[] {
    const photonApps = this._filteredPhotons.filter((p) => p.isApp);
    const externalApps = this._filteredExternalMCPs.filter((m) => m.hasMcpApp);
    return [...photonApps, ...externalApps];
  }

  private get _configured() {
    return this._filteredPhotons.filter((p) => !p.isApp && p.methods && p.methods.length > 0);
  }

  private get _needsSetup() {
    return this._filteredPhotons.filter((p) => !p.isApp && (!p.methods || p.methods.length === 0));
  }

  private get _filteredExternalMCPs() {
    let filtered = this.externalMCPs;

    // Filter by search query
    if (this._searchQuery.trim()) {
      const query = this._searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.name.toLowerCase().includes(query) ||
          (m.description && m.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }

  /** External MCPs that are NOT apps (apps go to _apps) */
  private get _nonAppExternalMCPs() {
    return this._filteredExternalMCPs.filter((m) => !m.hasMcpApp);
  }

  /** Sort items by recency — uses the order snapshot from page load,
   *  so the list stays stable during the session and only reorders on refresh. */
  private _sortByRecency<T extends { name: string }>(items: T[]): T[] {
    const recentIndex = new Map(this._initialRecentOrder.map((name, i) => [name, i]));
    return [...items].sort((a, b) => {
      const aIdx = recentIndex.get(a.name);
      const bIdx = recentIndex.get(b.name);
      if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
      if (aIdx !== undefined) return -1;
      if (bIdx !== undefined) return 1;
      return 0; // preserve original order for non-recent items
    });
  }

  /** @deprecated No longer used — recent items are sorted inline within their sections */
  private get _recentSet(): Set<string> {
    if (this._showFavoritesOnly || this._searchQuery) return new Set();
    return new Set(
      this._recentPhotons.filter((n) => this._filteredPhotons.some((p) => p.name === n))
    );
  }

  render() {
    return html`
      <nav class="sidebar-content" role="navigation" aria-label="Photon navigation">
        <div class="header">
          <div class="header-row">
            <button
              class="text-gradient logo"
              @click=${() =>
                this.dispatchEvent(new CustomEvent('home', { bubbles: true, composed: true }))}
              title="Go home"
            >
              <span class="logo-prefix">Photon </span>Beam
              <span
                class="status-indicator ${this.connected
                  ? 'connected'
                  : this.reconnecting
                    ? 'reconnecting'
                    : 'disconnected'}"
                title="${this.connected
                  ? 'Connected'
                  : this.reconnecting
                    ? 'Reconnecting...'
                    : 'Disconnected'}"
              ></span>
            </button>
            <button
              class="sidebar-minimize-btn"
              @click=${() => this.dispatchEvent(new CustomEvent('toggle-focus'))}
              title="Hide sidebar"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M9 3v18" />
                <path d="m16 15-3-3 3-3" />
              </svg>
            </button>
          </div>
          <div class="search-row">
            <div class="search-box" role="search">
              <input
                type="search"
                placeholder="Search photons... (⌘K)"
                .value=${this._searchQuery}
                @input=${(e: Event) => this._handleSearch(e)}
                @keydown=${(e: Event) => this._handleSearchKeydown(e as KeyboardEvent)}
                aria-label="Search photons"
              />
            </div>
            <button
              class="sidebar-minimize-btn create-btn"
              @click=${() => this._createPhoton()}
              title="Create new photon"
              aria-label="Create new photon"
            >
              ${plus}
            </button>
          </div>
          <div class="filter-row" role="group" aria-label="Filter options">
            <button
              class="filter-btn ${this._showFavoritesOnly ? 'active' : ''}"
              @click=${() => this._toggleFavoritesFilter()}
              title="Show favorites only (f)"
              aria-pressed="${this._showFavoritesOnly}"
              aria-label="Filter by favorites${this._favorites.size > 0
                ? ` (${this._favorites.size})`
                : ''}"
            >
              <span class="filter-icon-wrap">
                ${starFilled}
                ${this._favorites.size > 0
                  ? html`<span class="filter-count-in-star" aria-hidden="true"
                      >${this._favorites.size}</span
                    >`
                  : ''}
              </span>
              <span class="filter-btn-label">Favorites</span>
            </button>
            <button
              class="filter-btn"
              @click=${() => this.dispatchEvent(new CustomEvent('marketplace'))}
              aria-label="Open marketplace"
              title="Marketplace"
            >
              ${marketplaceIcon}<span class="filter-btn-label">Marketplace</span>
              ${this.updatesAvailable > 0
                ? html`<span class="update-badge">${this.updatesAvailable}</span>`
                : ''}
            </button>
            <button
              class="filter-btn"
              @click=${() => this.dispatchEvent(new CustomEvent('daemon'))}
              aria-label="Open Pulse panel"
              title="Pulse: scheduled jobs, webhooks, and sessions"
            >
              ${activityIcon}<span class="filter-btn-label">Pulse</span>
            </button>
          </div>
        </div>

        <div class="sidebar-scroll">
          ${(() => {
            const apps = this._sortByRecency(this._apps);
            const configured = this._sortByRecency(this._configured);
            return html`
              ${apps.length > 0
                ? this._renderSection('apps', 'APPS', apps, (p) =>
                    p.isExternalMCP
                      ? this._renderExternalMCPItem(p)
                      : this._renderPhotonItem(p, 'app')
                  )
                : ''}
              ${configured.length > 0
                ? this._renderSection('photons', 'PHOTONS', configured, (p) =>
                    this._renderPhotonItem(p, 'configured')
                  )
                : ''}
            `;
          })()}
          ${(() => {
            const needsConfig = this._needsSetup.filter((p) => p.errorReason !== 'load-error');
            const loadErrors = this._needsSetup.filter((p) => p.errorReason === 'load-error');
            return html`
              ${needsConfig.length > 0
                ? this._renderSection(
                    'config',
                    'NEEDS CONFIGURATION',
                    needsConfig,
                    (p) => this._renderPhotonItem(p, 'unconfigured'),
                    'attention',
                    warningIcon
                  )
                : ''}
              ${loadErrors.length > 0
                ? this._renderSection(
                    'errors',
                    'LOAD ERRORS',
                    loadErrors,
                    (p) => this._renderPhotonItem(p, 'unconfigured'),
                    'attention',
                    xMark
                  )
                : ''}
            `;
          })()}
          ${this._nonAppExternalMCPs.length > 0
            ? this._renderSection(
                'mcps',
                'MCPS',
                this._sortByRecency(this._nonAppExternalMCPs),
                (mcp) => this._renderExternalMCPItem(mcp)
              )
            : ''}
          ${this._apps.length === 0 &&
          this._configured.length === 0 &&
          this._needsSetup.length === 0 &&
          this._nonAppExternalMCPs.length === 0
            ? html`
                <div class="empty-state">
                  ${this._searchQuery.trim()
                    ? html`
                        <div class="empty-icon">${searchIcon}</div>
                        <div class="empty-title">No results</div>
                        <div class="empty-hint">No photons match "${this._searchQuery}"</div>
                      `
                    : html`
                        <div class="empty-icon">${packageBox}</div>
                        <div class="empty-title">No photons yet</div>
                        <div class="empty-hint">
                          Add photons from the marketplace or create your own
                        </div>
                        <button
                          class="empty-action"
                          @click=${() => this.dispatchEvent(new CustomEvent('marketplace'))}
                        >
                          ${marketplaceIcon} Browse Marketplace
                        </button>
                      `}
                </div>
              `
            : ''}
        </div>
      </nav>

      <div class="sidebar-footer">
        <button
          class="footer-link approval-badge-btn ${this.pendingApprovals === 0 ? 'disabled' : ''}"
          @click=${() =>
            this.pendingApprovals > 0 &&
            this.dispatchEvent(
              new CustomEvent('show-approvals', { bubbles: true, composed: true })
            )}
          title="${this.pendingApprovals > 0
            ? `Pending approvals (${this.pendingApprovals})`
            : 'No pending approvals'}"
          aria-label="Show pending approvals"
          ?disabled=${this.pendingApprovals === 0}
        >
          ${shieldCheck}
          <span class="approval-badge">${this.pendingApprovals}</span>
        </button>
        <button
          class="footer-link"
          @click=${() => this._showShortcuts()}
          title="Shortcuts"
          aria-label="Show keyboard shortcuts"
        >
          ${keyboardIcon}
        </button>
        <button
          class="footer-link"
          @click=${() =>
            this.dispatchEvent(
              new CustomEvent('open-theme-settings', { bubbles: true, composed: true })
            )}
          title="Theme"
          aria-label="Open theme settings"
        >
          ${palette}
        </button>
        <a
          class="footer-link"
          href="https://github.com/portel-dev/photon#readme"
          target="_blank"
          rel="noopener noreferrer"
          title="Docs"
          aria-label="Open documentation"
        >
          ${docs}
        </a>
      </div>
    `;
  }

  private _showShortcuts() {
    this.dispatchEvent(new CustomEvent('show-shortcuts', { bubbles: true, composed: true }));
  }

  private async _createPhoton() {
    const result = await elicit({
      ask: 'form',
      icon: '\u2728',
      message: 'Create a new photon',
      description:
        'Give your photon a name and we\u2019ll set up everything for you. ' +
        'Use lowercase letters and hyphens \u2014 like my-api or weather-bot.',
      submitLabel: 'Create',
      cancelLabel: 'Cancel',
      fields: [
        { name: 'name', label: 'Name', placeholder: 'e.g. my-api', required: true },
        {
          name: 'template',
          label: 'Template',
          type: 'text',
          enum: ['Hello World', 'API Tool', 'Stateful', 'App'],
          default: 'Hello World',
        },
      ],
    });
    if (result.action === 'cancel') return;
    const form = result.value as { name?: string; template?: string };
    if (!form?.name?.trim()) return;
    let filename = form.name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9.-]/g, '-');
    if (!filename.endsWith('.photon.ts')) {
      filename = filename.replace(/\.photon$/, '') + '.photon.ts';
    }
    const template = (form.template || 'Blank').toLowerCase().replace(/\s+/g, '-');
    this.dispatchEvent(
      new CustomEvent('create-photon', {
        detail: { name: filename, template },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _openStudio(photonName: string) {
    this.dispatchEvent(
      new CustomEvent('open-studio', {
        detail: { photonName },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _renderSection(
    key: string,
    label: string,
    items: PhotonItem[],
    renderItem: (item: PhotonItem) => unknown,
    headerClass = '',
    icon?: unknown
  ) {
    const collapsed = this._collapsedSections.has(key);
    const headerId = `${key}-header`;
    return html`
      <div
        class="section-header ${headerClass}"
        id=${headerId}
        @click=${() => this._toggleSection(key)}
        role="button"
        aria-expanded=${String(!collapsed)}
      >
        ${icon ? icon : ''}
        <span class="section-label">${label}</span>
        <span class="section-count">${items.length}</span>
        <svg
          class="section-chevron ${collapsed ? 'collapsed' : ''}"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5l3.5 3.5 3.5-3.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <ul
        class="photon-list motion-stagger ${collapsed ? 'collapsed' : ''}"
        role="listbox"
        aria-labelledby=${headerId}
        style="max-height: ${collapsed ? '0' : this._estimateSectionHeight(items) + 'px'}"
      >
        ${items.map(renderItem)}
      </ul>
    `;
  }

  private _renderPhotonItem(photon: PhotonItem, type: 'app' | 'configured' | 'unconfigured') {
    const methodCount = photon.methods?.length || 0;
    const isApp = type === 'app';
    const isUnconfigured = type === 'unconfigured';
    const isFavorited = this._favorites.has(photon.name);

    // Determine icon: use photon's icon if available, otherwise default
    const displayIcon =
      photon.icon || (isApp ? appDefault : photon.name.substring(0, 2).toUpperCase());
    const hasCustomIcon = !!photon.icon;
    const isEmoji = isApp || hasCustomIcon;

    // Generate a deterministic hue from photon name for initials icons
    let initialsStyle = '';
    if (!isEmoji) {
      let hash = 0;
      for (let i = 0; i < photon.name.length; i++) {
        hash = photon.name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360;
      const isLight = this.theme === 'light';
      initialsStyle = isLight
        ? `background: hsl(${hue}, 30%, 90%); color: hsl(${hue}, 50%, 35%);`
        : `background: hsl(${hue}, 35%, 22%); color: hsl(${hue}, 60%, 75%);`;
    }

    const isSelected = this.selectedPhoton === photon.name;

    return html`
      <li
        class="photon-item ${isSelected ? 'active' : ''} ${photon.internal
          ? 'internal'
          : ''} ${this._isPhotonWarm(photon.name) ? 'warmth' : ''}"
        role="option"
        aria-selected="${isSelected}"
        tabindex="0"
        @click=${() => this._selectPhoton(photon)}
        @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._selectPhoton(photon)}
        title="${photon.description || photon.name}${photon.path ? `\n${photon.path}` : ''}"
      >
        <div
          class="photon-icon ${isEmoji ? 'emoji-icon' : ''}"
          style="${initialsStyle}"
          aria-hidden="true"
        >
          ${displayIcon}
        </div>
        <div class="photon-item-body">
          <div class="photon-main-row">
            <div class="photon-info">
              <div class="photon-name">${photon.name}</div>
              ${photon.internal ? html`<span class="internal-badge">System</span>` : ''}
            </div>
            <button
              class="star-btn ${isFavorited ? 'favorited' : ''}"
              @click=${(e: Event) => this._toggleFavorite(e, photon.name)}
              title="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}"
              aria-label="${isFavorited
                ? `Remove ${photon.name} from favorites`
                : `Add ${photon.name} to favorites`}"
              aria-pressed="${isFavorited}"
            >
              ${isFavorited ? starFilled : starOutline}
            </button>
            ${isUnconfigured
              ? photon.errorReason === 'load-error'
                ? html`<span class="method-count error" aria-label="Error loading">×</span>`
                : html`<span class="method-count unconfigured" aria-label="Needs configuration"
                    >?</span
                  >`
              : this._renderCountsPill(photon, methodCount)}
          </div>
          ${isSelected ? this._renderInlineViewTabs() : ''}
        </div>
      </li>
    `;
  }

  private _renderExternalMCPItem(mcp: PhotonItem) {
    const methodCount = mcp.methods?.length || 0;
    const isConnected = mcp.connected !== false;
    const displayIcon = mcp.icon || plug;
    const isFavorited = this._favorites.has(mcp.name);
    const isSelected = this.selectedPhoton === mcp.name;

    return html`
      <li
        class="photon-item ${isSelected ? 'active' : ''} ${!isConnected ? 'disconnected' : ''}"
        role="option"
        aria-selected="${isSelected}"
        tabindex="0"
        @click=${() => this._selectPhoton(mcp)}
        @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._selectPhoton(mcp)}
        title="${mcp.description || 'External MCP server'}${mcp.errorMessage
          ? `\n⚠️ ${mcp.errorMessage}`
          : ''}"
      >
        <div class="photon-icon external-mcp-icon" aria-hidden="true">${displayIcon}</div>
        <div class="photon-item-body">
          <div class="photon-main-row">
            <div class="photon-info">
              <div class="photon-name">${mcp.name}</div>
            </div>
            <button
              class="star-btn ${isFavorited ? 'favorited' : ''}"
              @click=${(e: Event) => this._toggleFavorite(e, mcp.name)}
              title="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}"
              aria-label="${isFavorited
                ? `Remove ${mcp.name} from favorites`
                : `Add ${mcp.name} to favorites`}"
              aria-pressed="${isFavorited}"
            >
              ${isFavorited ? starFilled : starOutline}
            </button>
            ${!isConnected
              ? html`
                  <span class="disconnect-badge" title="${mcp.errorMessage || 'Disconnected'}"
                    >Offline</span
                  >
                  <button
                    class="reconnect-btn"
                    @click=${(e: Event) => this._reconnectMCP(e, mcp.name)}
                    title="Reconnect"
                    aria-label="Reconnect ${mcp.name}"
                  >
                    ↻
                  </button>
                `
              : this._renderCountsPill(mcp, methodCount)}
          </div>
          ${isSelected ? this._renderInlineViewTabs() : ''}
        </div>
      </li>
    `;
  }

  private _renderInlineViewTabs() {
    const appTabIcon = html`<svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>`;

    const methodsTabIcon = html`<svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>`;

    const tabs = [
      ...(this.isApp ? [{ id: 'app', label: 'App', icon: appTabIcon }] : []),
      { id: 'methods', label: 'Methods', icon: methodsTabIcon },
      { id: 'log', label: 'Pulse', icon: activityIcon },
      ...(this.hasSettings || this.hasSetup
        ? [{ id: 'settings', label: 'Settings', icon: settingsIcon }]
        : []),
      ...(this.hasPath && !this.isExternalMCP
        ? [{ id: 'source', label: 'Source', icon: sourceIcon }]
        : []),
      { id: 'help', label: 'Help', icon: docs },
    ];

    return html`
      <div class="photon-view-tabs" role="tablist" aria-label="Selected photon views">
        ${tabs.map(
          (tab) => html`
            <button
              class="photon-view-tab ${this.mainTab === tab.id ? 'active' : ''}"
              role="tab"
              aria-selected=${String(this.mainTab === tab.id)}
              title=${tab.label}
              aria-label=${tab.label}
              @click=${(e: Event) => {
                e.stopPropagation();
                this._emitTabChange(tab.id);
              }}
            >
              ${tab.icon}
            </button>
          `
        )}
      </div>
    `;
  }

  private _estimateSectionHeight(items: PhotonItem[]) {
    return items.reduce((height, item) => {
      const isSelected = item.name === this.selectedPhoton;
      return height + (isSelected ? 92 : 52);
    }, 0);
  }

  private _reconnectMCP(e: Event, mcpName: string) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('reconnect-mcp', {
        detail: { name: mcpName },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _renderCountsPill(photon: PhotonItem, toolCount: number) {
    const promptCount = photon.promptCount || 0;
    const resourceCount = photon.resourceCount || 0;
    // Prompt templates are added as methods, so subtract to avoid double counting
    const actualToolCount = Math.max(0, toolCount - promptCount);
    const hasAnyCounts = actualToolCount > 0 || promptCount > 0 || resourceCount > 0;

    if (!hasAnyCounts) return '';

    const tooltipParts = [
      actualToolCount > 0 ? `${actualToolCount} ${actualToolCount === 1 ? 'tool' : 'tools'}` : '',
      promptCount > 0 ? `${promptCount} ${promptCount === 1 ? 'prompt' : 'prompts'}` : '',
      resourceCount > 0 ? `${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}` : '',
    ].filter(Boolean);

    return html`<span
      class="counts-pill"
      aria-label="${tooltipParts.join(', ')}"
      title="${tooltipParts.join(', ')}"
    >
      ${photon.hasUpdate
        ? html`<span class="update-dot" title="Update available"
            ><span class="visually-hidden">Update available</span></span
          >`
        : ''}
      ${actualToolCount > 0 ? html`<span class="count-tools">${actualToolCount}</span>` : ''}
      ${promptCount > 0 && actualToolCount > 0 ? html`<span class="count-sep">·</span>` : ''}
      ${promptCount > 0 ? html`<span class="count-prompts">${promptCount}</span>` : ''}
      ${resourceCount > 0 && (actualToolCount > 0 || promptCount > 0)
        ? html`<span class="count-sep">·</span>`
        : ''}
      ${resourceCount > 0 ? html`<span class="count-resources">${resourceCount}</span>` : ''}
    </span>`;
  }

  private _setTheme(theme: Theme) {
    this.dispatchEvent(
      new CustomEvent('theme-change', {
        detail: { theme },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitTabChange(tab: string) {
    this.dispatchEvent(new CustomEvent('tab-change', { detail: { tab } }));
  }

  private _handleSearch(e: Event) {
    this._searchQuery = (e.target as HTMLInputElement).value;
  }

  private _handleSearchKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this._searchQuery = '';
      this._searchInput.value = '';
      this._searchInput.blur();
    }
  }

  private _selectPhoton(photon: PhotonItem) {
    this.dispatchEvent(new CustomEvent('select', { detail: { photon } }));
  }

  private _openSettings(e: Event, photonName: string) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('photon-settings', {
        detail: { name: photonName },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _toggleFavorite(e: Event, photonName: string) {
    e.stopPropagation(); // Don't select the photon
    const newFavorites = new Set(this._favorites);
    if (newFavorites.has(photonName)) {
      newFavorites.delete(photonName);
    } else {
      newFavorites.add(photonName);
    }
    this._favorites = newFavorites;
    this._saveFavorites();
  }

  private _toggleFavoritesFilter() {
    this._showFavoritesOnly = !this._showFavoritesOnly;
  }

  // Public methods for keyboard navigation from parent
  focusSearch() {
    this._searchInput?.focus();
  }

  clearSearch() {
    this._searchQuery = '';
    if (this._searchInput) {
      this._searchInput.value = '';
    }
  }

  getAllPhotons(): PhotonItem[] {
    return this._filteredPhotons;
  }

  toggleFavoritesFilter() {
    this._toggleFavoritesFilter();
  }

  isFavoritesFilterActive(): boolean {
    return this._showFavoritesOnly;
  }

  scrollPhotonIntoView(name: string) {
    this._scrollPhotonIntoView(name, true);
  }

  private _scrollPhotonIntoView(name: string, flashHighlight: boolean) {
    void this.updateComplete.then(() => {
      const items = this.shadowRoot?.querySelectorAll('.photon-item');
      if (!items) return;
      for (const item of items) {
        const nameEl = item.querySelector('.photon-name');
        if (nameEl?.textContent?.trim() === name) {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          if (flashHighlight) {
            (item as HTMLElement).classList.add('flash-highlight');
            setTimeout(() => (item as HTMLElement).classList.remove('flash-highlight'), 600);
          }
          break;
        }
      }
    });
  }

  private _scrollSidebarToTop() {
    void this.updateComplete.then(() => {
      const scrollEl = this.shadowRoot?.querySelector('.sidebar-scroll');
      if (scrollEl) {
        scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  /**
   * Check if a photon currently has a warmth indicator (notification received within last 5 seconds)
   */
  private _isPhotonWarm(photonName: string): boolean {
    const lastNotificationTime = this._notificationWarmth.get(photonName);
    if (!lastNotificationTime) return false;
    return Date.now() - lastNotificationTime < 5000;
  }

  /**
   * Update the warmth indicator for a photon when a notification arrives
   */
  updatePhotonWarmth(photonName: string) {
    this._notificationWarmth.set(photonName, Date.now());
    this.requestUpdate();
  }

  /**
   * Check if a notification type is watched by a photon
   * This will be called by beam-app to determine if window.focus() should be triggered
   */
  isNotificationWatched(photonName: string, notificationType: string): boolean {
    // TODO: Once photon metadata is available in the sidebar, check the @notify-on subscriptions
    // For now, return true for any notification from any photon to always bring window to focus
    return true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'beam-sidebar': BeamSidebar;
  }
}
